import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UserCircleIcon,
  CalendarDaysIcon,
  ChatBubbleBottomCenterTextIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import moment from 'moment';

// --- START: MOCK DATA ---
const mockEmployees = [
  { id: 1, name: 'Aditya Verma' },
  { id: 2, name: 'Pooja Singh' },
  { id: 3, name: 'Kunal Kapoor' },
  { id: 4, name: 'Neha Reddy' },
];

const mockFeedbackRequests = [
  { id: 1, reviewee: 'Aditya Verma', reviewer: 'Pooja Singh', dueDate: '2025-10-15', status: 'Pending' },
  { id: 2, reviewee: 'Aditya Verma', reviewer: 'Kunal Kapoor', dueDate: '2025-10-15', status: 'Completed' },
  { id: 3, reviewee: 'Neha Reddy', reviewer: 'Aditya Verma', dueDate: '2025-11-01', status: 'Pending' },
];
// --- END: MOCK DATA ---

const ratingOptions = [
  { value: '5', label: 'Exceptional' },
  { value: '4', label: 'Strong' },
  { value: '3', label: 'Average' },
  { value: '2', label: 'Below Average' },
  { value: '1', label: 'N/A' },
];

const Feedback360 = () => {
  const [feedbackRequests, setFeedbackRequests] = useState(mockFeedbackRequests);
  const [newRequestForm, setNewRequestForm] = useState({
    revieweeId: '',
    reviewerId: '',
    dueDate: '',
  });
  const [submissionForm, setSubmissionForm] = useState({
    teamwork: '3',
    leadership: '3',
    problemSolving: '3',
    overallComments: '',
  });

  const [isRequestSubmitted, setIsRequestSubmitted] = useState(false);
  const [isFeedbackSubmitted, setIsFeedbackSubmitted] = useState(false);

  const handleRequestChange = (e) => {
    const { name, value } = e.target;
    setNewRequestForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmissionChange = (e) => {
    const { name, value } = e.target;
    setSubmissionForm(prev => ({ ...prev, [name]: value }));
  };

  const handleRequestSubmit = (e) => {
    e.preventDefault();
    if (!newRequestForm.revieweeId || !newRequestForm.reviewerId || !newRequestForm.dueDate) {
      alert('Please fill out all fields.');
      return;
    }

    const revieweeName = mockEmployees.find(emp => emp.id === parseInt(newRequestForm.revieweeId))?.name;
    const reviewerName = mockEmployees.find(emp => emp.id === parseInt(newRequestForm.reviewerId))?.name;

    const newRequest = {
      id: feedbackRequests.length + 1,
      reviewee: revieweeName,
      reviewer: reviewerName,
      dueDate: newRequestForm.dueDate,
      status: 'Pending',
    };

    setFeedbackRequests([newRequest, ...feedbackRequests]);
    setNewRequestForm({ revieweeId: '', reviewerId: '', dueDate: '' });
    setIsRequestSubmitted(true);
    setTimeout(() => setIsRequestSubmitted(false), 3000);
  };

  const handleFeedbackSubmit = (e) => {
    e.preventDefault();
    console.log('Feedback Submitted:', submissionForm);
    setIsFeedbackSubmitted(true);
    setTimeout(() => setIsFeedbackSubmitted(false), 3000);
    // In a real app, you'd update a specific request's status to 'Completed' here.
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending': return 'bg-yellow-100 text-yellow-600';
      case 'Completed': return 'bg-green-100 text-green-600';
      case 'Overdue': return 'bg-red-100 text-red-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-100 p-8 text-gray-800">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-7xl mx-auto mb-8"
      >
        <h1 className="text-4xl font-extrabold text-purple-800">360-Degree Feedback</h1>
        <p className="text-xl font-light text-purple-600 mt-2">
          Gather and manage multi-source performance feedback.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-7xl mx-auto">
        {/* Feedback Request Form */}
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          className="lg:col-span-1 bg-white p-6 rounded-3xl shadow-lg h-full flex flex-col"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Request Feedback</h3>
          <p className="text-sm text-gray-500 mb-4">
            *All feedback is confidential and will only be shared with the employee's manager in a summarized format.*
          </p>
          <form onSubmit={handleRequestSubmit} className="space-y-4">
            <div>
              <label htmlFor="revieweeId" className="block text-sm font-medium text-gray-700">Employee to be Reviewed</label>
              <select
                name="revieweeId"
                value={newRequestForm.revieweeId}
                onChange={handleRequestChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                required
              >
                <option value="">Select Employee</option>
                {mockEmployees.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="reviewerId" className="block text-sm font-medium text-gray-700">Reviewer</label>
              <select
                name="reviewerId"
                value={newRequestForm.reviewerId}
                onChange={handleRequestChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                required
              >
                <option value="">Select Reviewer</option>
                {mockEmployees.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700">Due Date</label>
              <input
                type="date"
                name="dueDate"
                value={newRequestForm.dueDate}
                onChange={handleRequestChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                required
              />
            </div>
            <button
              type="submit"
              className="mt-6 w-full flex justify-center items-center py-3 px-4 rounded-full shadow-lg text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <ChatBubbleBottomCenterTextIcon className="h-5 w-5 mr-2" />
              Send Feedback Request
            </button>
            <AnimatePresence>
              {isRequestSubmitted && (
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mt-2 text-center text-sm font-semibold text-green-700 bg-green-100 rounded-full py-1"
                >
                  Request sent successfully!
                </motion.p>
              )}
            </AnimatePresence>
          </form>
        </motion.div>

        {/* Feedback Submission Form (Simulated) */}
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.2 }}
          className="lg:col-span-1 bg-white p-6 rounded-3xl shadow-lg h-full flex flex-col"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Submit Feedback for a Colleague</h3>
          <form onSubmit={handleFeedbackSubmit} className="space-y-4">
            {['teamwork', 'leadership', 'problemSolving'].map((field) => (
              <div key={field} className="border p-4 rounded-xl bg-gray-50">
                <h4 className="text-lg font-bold text-gray-900 mb-2 capitalize">{field.replace(/([A-Z])/g, ' $1')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Rating</label>
                    <select
                      name={field}
                      value={submissionForm[field]}
                      onChange={handleSubmissionChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                    >
                      {ratingOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Comments</label>
                    <textarea
                      name={`comments-${field}`}
                      rows="3"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                      placeholder="Provide specific, constructive examples..."
                    ></textarea>
                  </div>
                </div>
              </div>
            ))}
            <button
              type="submit"
              className="mt-6 w-full flex justify-center items-center py-3 px-4 rounded-full shadow-lg text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <CheckCircleIcon className="h-5 w-5 mr-2" />
              Submit Feedback
            </button>
            <AnimatePresence>
              {isFeedbackSubmitted && (
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mt-2 text-center text-sm font-semibold text-green-700 bg-green-100 rounded-full py-1"
                >
                  Feedback submitted. Thank you!
                </motion.p>
              )}
            </AnimatePresence>
          </form>
        </motion.div>

        {/* Feedback Status Table */}
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.4 }}
          className="lg:col-span-2 bg-white p-6 rounded-3xl shadow-lg mt-6"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Feedback Status</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reviewer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <AnimatePresence>
                  {feedbackRequests.map((req) => (
                    <motion.tr
                      key={req.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{req.reviewee}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{req.reviewer}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{moment(req.dueDate).format('MMM D, YYYY')}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(req.status)}`}>
                          {req.status}
                        </span>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Feedback360;